import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react"
import table from "./styling/table"
import button from "./styling/button"
import popover from "./styling/popover"
import select from "./styling/select"
import menu from "./styling/menu"
import breadcrumb from "./styling/breadcrumb"
import segment from "./styling/segment"
import link from "./styling/link"
import editable from "./styling/editable"
import switchRecipe from "./styling/switch"
import accordionRecipe from "./styling/accordion"
import tabRecipe from "./styling/tab"
import card from "./styling/card"
import skeleton from "./styling/skeleton"

export const config = defineConfig({
  globalCss: {
    ":where(:host > html#section-body, :root:has(div#section-body))": {
      fontSize: "15px",
    },
    "button, [role='button']": {
      fontSize: "16px",
    },
    "input, textarea, select": {
      bg: "card !important",
    },
  },
  theme: {
    breakpoints: {
      sm: "680px",
      md: "968px",
      lg: "1224px",
      xl: "1480px",
      "2xl": "1736px",
      "3xl": "2120px",
      "4xl": "2760px",
    },
    // Keyframes go here, not in a `css` prop: Chakra drops their property names there
    keyframes: {
      // The custom alert editor's condition rows and groups (assets/.../CustomAlertEditor.tsx)
      conditionAdded: {
        "0%, 40%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0.75)", background: "rgba(57, 174, 210, 0.18)" },
        "100%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0)", background: "rgba(57, 174, 210, 0)" },
      },
      conditionLanded: {
        "0%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0.75)", background: "rgba(57, 174, 210, 0.18)" },
        "100%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0)", background: "rgba(57, 174, 210, 0)" },
      },
      contradictionGlow: {
        "0%, 100%": { boxShadow: "0 0 0 2px rgba(229, 62, 62, 0.45)" },
        "50%": { boxShadow: "0 0 0 2px rgba(229, 62, 62, 0.9), 0 0 10px rgba(229, 62, 62, 0.45)" },
      },
      // Alert settings cards (shared/.../alerts/AlertSettingsCard.tsx); each keeps the card's
      // drop shadow, which the animated box-shadow would otherwise replace
      alertCardHighlight: {
        "0%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0.7), 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)" },
        "100%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0), 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)" },
      },
      alertCardEditing: {
        "0%, 100%": { boxShadow: "0 0 0 2px rgba(57, 174, 210, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)" },
        "50%": { boxShadow: "0 0 0 4px rgba(57, 174, 210, 0.45), 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)" },
      },
      groupLanded: {
        "0%": { boxShadow: "0 0 0 0 rgba(57, 174, 210, 0.7)", background: "rgba(57, 174, 210, 0.22)", borderColor: "#39aed2" },
        "100%": { boxShadow: "0 0 0 10px rgba(57, 174, 210, 0)", background: "rgba(57, 174, 210, 0)", borderColor: "var(--planner-colors-alert-group_boundary)" },
      },
      // A group just added: holds, then fades with its new condition's conditionAdded glow
      groupAdded: {
        "0%, 40%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0.6)", background: "rgba(57, 174, 210, 0.14)", borderColor: "#39aed2" },
        "100%": { boxShadow: "0 0 0 3px rgba(57, 174, 210, 0)", background: "rgba(57, 174, 210, 0)", borderColor: "var(--planner-colors-alert-group_boundary)" },
      },
    },
    tokens: {
      colors: {
        highlight: { value: "#0bc5ea" },
        active: { value: "#a170d9" },
        error: { value: "#d2042d" },
        error_light: { value: "#ea0b3b" },
        blue: { value: "#39aed2" },
        grey: { value: "#a9a9a9" },
        grey_light: { value: "rgba(102, 102, 102, 0.4)" },
        grey_dark: { value: "rgba(102, 102, 102, 0.8)" },
        blue_dark: { value: "#19596d" },
        blue_light: { value: "#90d2e6" },
        red: { value: "#d2042d" },
        sidebar: { value: "#2D2860" },

        solar: { value: "#10B981" },
        fuel: { value: "#8B5CF6" },
        genset: { value: "#64748B" },
        battery: { value: "#F59E0B" },
        distro: { value: "#2563EB" },
      },
      fonts: {
        heading: { value: "montserrat" },
        body: { value: "montserrat" },
        mono: { value: "montserrat" },
        global: { value: "montserrat" },
      },
      shadows: {
        card: { value: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)" },
      },
      radii: {
        card: { value: "5px" },
      },
      zIndex: {
        toast: { value: 1700 },
        dialog: { value: 1800 },
      },
    },
    // Colours that change with the Colour Mode. Always give `_light` and `_dark`,
    // never `base`: `base` values are emitted on `cssVarsRoot` (`#root` in the hub),
    // which would re-declare them below `<html class="dark">` and hide the dark value.
    // Light values match the previous flat tokens, so light mode looks the same.
    semanticTokens: {
      colors: {
        primary: { value: { _light: "#2B2B2B", _dark: "#F2F0EF" } },
        primary_light: { value: { _light: "#545454", _dark: "#C3C2B7" } },
        primary_muted: { value: { _light: "#8A8A8A", _dark: "#9A998F" } },
        background: { value: { _light: "#F2F0EF", _dark: "#1A1A19" } },
        card: { value: { _light: "white", _dark: "#242423" } },
        divider: { value: { _light: "#D0D0D0", _dark: "#3A3936" } },
        divider_subtle: { value: { _light: "#eeeeee", _dark: "#3A3936" } },
        // The hub's side and top nav outlines: darker than the page in dark mode, so the chrome
        // sits back rather than being ringed in a pale line
        nav_border: { value: { _light: "#D0D0D0", _dark: "#0B0B0A" } },
        table_header: { value: { _light: "#DCDCDC", _dark: "#363532" } },
        // Loading placeholders (styling/skeleton.ts): Chakra's light grey as before, and in dark a
        // grey that stands clear of both the page and a card
        skeleton: { value: { _light: "{colors.gray.200}", _dark: "#4A4945" } },
        // Alert cards and the custom alert editor (shared/.../alerts, assets/.../asset). Light
        // values are the design's, unchanged.
        alert: {
          edit_divider: { value: { _light: "#CDEAF3", _dark: "#2A4A55" } },
          unit_bg: { value: { _light: "#EBEBEB", _dark: "#33322F" } },
          group_boundary: { value: { _light: "#DADADA", _dark: "#4A4945" } },
          bracket: { value: { _light: "#a9a9a9", _dark: "#77766F" } },
          locked_icon: { value: { _light: "#C4C4C4", _dark: "#5A5954" } },
          add_row_border: { value: { _light: "#9ED6E8", _dark: "#2F6A7D" } },
          step_hover: { value: { _light: "#EAF7FA", _dark: "#1F3A42" } },
        },
        // The site's outlined chips (components/Chip): configuration state and alert tags
        chip: {
          active: {
            bg: { value: { _light: "rgba(198,246,213,.45)", _dark: "#1C4532" } },
            fg: { value: { _light: "#22543D", _dark: "#9AE6B4" } },
            border: { value: { _light: "#68D391", _dark: "#2F855A" } },
          },
          inactive: {
            bg: { value: { _light: "#F4F4F4", _dark: "#2D2C2A" } },
            fg: { value: { _light: "#545454", _dark: "#C3C2B7" } },
            border: { value: { _light: "#D0D0D0", _dark: "#3A3936" } },
          },
          info: {
            fg: { value: { _light: "#1f7a44", _dark: "#9AE6B4" } },
            border: { value: { _light: "#50c878", _dark: "#38A169" } },
          },
          warning: {
            fg: { value: { _light: "#b3541e", _dark: "#FBD38D" } },
            border: { value: { _light: "#f0986f", _dark: "#C05621" } },
          },
          critical: {
            fg: { value: { _light: "#d2042d", _dark: "#FEB2B2" } },
            border: { value: { _light: "#d2042d", _dark: "#E53E3E" } },
          },
        },
        // Chart axes: recharts paints tick labels with the axis stroke, so dark mode
        // needs a lighter value than `border` for the labels to stay readable.
        chart_axis: { value: { _light: "{colors.gray.200}", _dark: "#8F8E86" } },
        // Inner disc of the energy flow diagram's hub circles (dashboards/.../flow). Dark is a
        // deep teal so the light text and ring values on it stay readable.
        flow_hub: { value: { _light: "rgba(234, 247, 250, 0.8)", _dark: "#1F3A42" } },
        // Rows inside an expanded table row (hybrid children) and their tree strip.
        row_expanded: { value: { _light: "#F1F5F9", _dark: "#1E1E1D" } },
        row_expanded_hover: { value: { _light: "#E2E8F0", _dark: "#2C2B29" } },
        row_nested: { value: { _light: "#F8FAFC", _dark: "#1E1E1D" } },
        row_divider: { value: { _light: "#E2E8F0", _dark: "#3A3936" } },
        tree_strip: { value: { _light: "#CBD5E1", _dark: "#2F2E2C" } },
        tree_line: { value: { _light: "#94A3B8", _dark: "#6B6A63" } },

        // Chakra's own surfaces, used by its dialogs, menus and inputs. Light keeps
        // Chakra's defaults; dark uses the Logiq Systems warm greys.
        bg: {
          DEFAULT: { value: { _light: "{colors.white}", _dark: "#1A1A19" } },
          panel: { value: { _light: "{colors.white}", _dark: "#242423" } },
        },
        fg: {
          DEFAULT: { value: { _light: "{colors.black}", _dark: "#F2F0EF" } },
        },
        border: {
          DEFAULT: { value: { _light: "{colors.gray.200}", _dark: "#3A3936" } },
        },

        // Visual vocabulary chip pairs (docs/architecture/ui-visual-vocabulary.md).
        // Dark inverts each pair: deep tint behind, light text in front.
        severity: {
          critical: {
            bg: { value: { _light: "#FED7D7", _dark: "#63171B" } },
            fg: { value: { _light: "#9B2C2C", _dark: "#FEB2B2" } },
          },
          warning: {
            bg: { value: { _light: "#FEEBC8", _dark: "#652B19" } },
            fg: { value: { _light: "#7B341E", _dark: "#FBD38D" } },
          },
          info: {
            bg: { value: { _light: "#BEE3F8", _dark: "#1A365D" } },
            fg: { value: { _light: "#2B6CB0", _dark: "#90CDF4" } },
          },
          ok: {
            bg: { value: { _light: "#C6F6D5", _dark: "#1C4532" } },
            fg: { value: { _light: "#22543D", _dark: "#9AE6B4" } },
          },
        },
        lifecycle: {
          new: {
            bg: { value: { _light: "#FED7D7", _dark: "#63171B" } },
            fg: { value: { _light: "{colors.red.800}", _dark: "#FEB2B2" } },
          },
          acknowledged: {
            bg: { value: { _light: "#BEE3F8", _dark: "#1A365D" } },
            fg: { value: { _light: "{colors.blue.800}", _dark: "#90CDF4" } },
          },
          attended: {
            bg: { value: { _light: "#E9D8FD", _dark: "#322659" } },
            fg: { value: { _light: "{colors.purple.800}", _dark: "#D6BCFA" } },
          },
          resolved: {
            bg: { value: { _light: "#C6F6D5", _dark: "#1C4532" } },
            fg: { value: { _light: "{colors.green.800}", _dark: "#9AE6B4" } },
          },
        },
        scope: {
          neutral: {
            bg: { value: { _light: "#EDF2F7", _dark: "#2D3748" } },
            fg: { value: { _light: "#4A5568", _dark: "#CBD5E0" } },
          },
        },
      },
    },
    recipes: {
      button,
      link,
      skeleton,
    },
    slotRecipes: {
      table,
      popover,
      select,
      menu,
      breadcrumb,
      segment,
      editable,
      switch: switchRecipe,
      accordion: accordionRecipe,
      tabs: tabRecipe,
      card,
    },
  },
  cssVarsRoot: ":where(:host > html#section-body, :root:has(div#section-body))",
  cssVarsPrefix: "planner",
  disableLayers: true,
  strictTokens: false,
})

/** Default system for Chakra typegen (expects `$$chakra` on default export). */
export default createSystem(defaultConfig, config)
